/**
 * voice-enrollment Web から呼び出す内部 API クライアント。
 *
 * 実体の API は別アプリ（`apps/api/voice-enrollment/`）に存在し、
 * `https://api-voice-enrollment.air-closet.ai/v1/*` で公開される。
 * voice-enrollment Web から `apps/pipeline/voice-diarization/` への直接依存は **持たない** —
 * 全てのバックエンド処理は voice-enrollment-api 経由でこの API クライアントから呼ぶ。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web から voice-enrollment-api /v1/* を呼ぶ HTTP クライアント。直接 pipeline には依存しない
 * @graph-connects voice-enrollment-api [calls] /v1/* HTTP API
 */

import { logError } from '@cortex/faro';
import type { LanguageCode } from './i18n/index.js';

export interface EnrollmentStatus {
  email: string;
  /** 登録済み embedding がある言語 */
  enrolledLanguages: LanguageCode[];
  /** 言語別の最新登録日時 ISO8601 */
  enrolledAtByLanguage: Partial<Record<LanguageCode, string>>;
}

export interface EnrollmentResult {
  email: string;
  language: LanguageCode;
  /** サーバー側で算出した embedding の品質スコア */
  qualityScore: number;
  /** 何個の録音から平均化したか */
  sampleCount: number;
}

export interface EnrollmentSubmission {
  language: LanguageCode;
  /** 録音ごとの (scriptId, blob) ペア */
  recordings: { scriptId: string; blob: Blob }[];
  /** 同意したプライバシーポリシーのバージョン */
  consentVersion: string;
}

export interface ApiClient {
  fetchStatus(): Promise<EnrollmentStatus>;
  submitEnrollment(submission: EnrollmentSubmission): Promise<EnrollmentResult>;
  deleteEnrollment(language: LanguageCode): Promise<void>;
}

interface CreateApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business API レスポンスエラー型。status/code/message を保持して呼び出し側で UI 表示に使う
 * @graph-connects none
 */
class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business HTTP error レスポンスから error.code と message を取り出して ApiClientError を投げる。JSON でない body にも対応
 * @graph-connects none
 */
async function parseError(response: Response): Promise<never> {
  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    // エラーレスポンス本体が JSON でない場合は code/message を fallback で組み立てる
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'error_response_body_parse_failed',
    });
    body = undefined;
  }
  const code =
    typeof body === 'object' &&
    body &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error
      ? ((body.error as { code?: string }).code ?? 'UNKNOWN')
      : 'UNKNOWN';
  const message =
    typeof body === 'object' &&
    body &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error
      ? ((body.error as { message?: string }).message ?? response.statusText)
      : response.statusText;
  throw new ApiClientError(response.status, code, message);
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment-api を叩く ApiClient を生成する。fetch / baseUrl をテストで差し替え可能にする
 * @graph-connects voice-enrollment-api [calls] /v1/* HTTP API
 */
export function createApiClient(options: CreateApiClientOptions = {}): ApiClient {
  if (!options.baseUrl) {
    throw new Error('createApiClient: baseUrl is required');
  }
  const baseUrl = options.baseUrl.replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);

  return {
    async fetchStatus(): Promise<EnrollmentStatus> {
      const response = await fetchImpl(`${baseUrl}/status`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        await parseError(response);
      }
      return (await response.json()) as EnrollmentStatus;
    },

    async submitEnrollment(submission: EnrollmentSubmission): Promise<EnrollmentResult> {
      const form = new FormData();
      form.set('language', submission.language);
      form.set('consentVersion', submission.consentVersion);
      for (const rec of submission.recordings) {
        form.append(`recording[${rec.scriptId}]`, rec.blob, `${rec.scriptId}.webm`);
      }
      const response = await fetchImpl(`${baseUrl}/enroll`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!response.ok) {
        await parseError(response);
      }
      return (await response.json()) as EnrollmentResult;
    },

    async deleteEnrollment(language: LanguageCode): Promise<void> {
      const response = await fetchImpl(`${baseUrl}/enrollments/${language}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        await parseError(response);
      }
    },
  };
}

export { ApiClientError };
